import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/shared/Pagination';
import FilterBar, { dateRangeForFilter, DEFAULT_DATE_PRESETS } from '../components/shared/FilterBar';
import PaymentActionMenu from '../components/shared/PaymentActionMenu';
// Cheque column was removed — upload now lives inside PaymentActionMenu.
import { useResizableColumns, ResizeHandle, useSortableData, SortableHeader } from '../components/shared/tableUtils';

const defaultInvoices = [
  { id: 'INV-0012', caseName: 'Smith vs ABC Realty', mediator: 'Jim Richard', invoice: 'SRC-2025-001', invoiceTo: 'john@email.com', date: 'Apr 12, 2026', amount: '$10,500', status: 'Paid' },
  { id: 'INV-0013', caseName: 'Property LLC vs Doe', mediator: 'Jace Nelson', invoice: 'SRC-2025-002', invoiceTo: 'jane@firm.com', date: 'Apr 18, 2026', amount: '$12,000', status: 'Pending' },
  { id: 'INV-0014', caseName: 'Residential Co vs Wilson', mediator: 'Philip Snyder', invoice: 'SRC-2025-003', invoiceTo: 'bob@wilson.com', date: 'Mar 28, 2026', amount: '$8,800', status: 'Paid' },
  { id: 'INV-0012', caseName: 'Smith vs ABC Realty', mediator: 'Alex Groke', invoice: 'SRC-2025-001', invoiceTo: 'john@email.com', date: 'Apr 12, 2026', amount: '$8,800', status: 'Paid' },
  { id: 'INV-0013', caseName: 'Property LLC vs Doe', mediator: 'Luke Dalton', invoice: 'SRC-2025-002', invoiceTo: 'jane@firm.com', date: 'Apr 18, 2026', amount: '$9,000', status: 'Pending' },
  { id: 'INV-0014', caseName: 'Residential Co vs Wilson', mediator: 'Jordan Glover', invoice: 'SRC-2025-003', invoiceTo: 'bob@wilson.com', date: 'Mar 28, 2026', amount: '$11,800', status: 'Paid' },
  { id: 'INV-0012', caseName: 'Smith vs ABC Realty', mediator: 'Stephan Asher', invoice: 'SRC-2025-001', invoiceTo: 'john@email.com', date: 'Apr 12, 2026', amount: '$8,600', status: 'Paid' },
  { id: 'INV-0013', caseName: 'Property LLC vs Doe', mediator: 'Gary Beil', invoice: 'SRC-2025-002', invoiceTo: 'jane@firm.com', date: 'Apr 18, 2026', amount: '$7,400', status: 'Pending' },
  { id: 'INV-0014', caseName: 'Residential Co vs Wilson', mediator: 'Zayn Walls', invoice: 'SRC-2025-003', invoiceTo: 'bob@wilson.com', date: 'Mar 28, 2026', amount: '$9,900', status: 'Paid' },
];

/* ---- icon helpers ---- */
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6.42" cy="6.42" r="4.67" stroke="#9ca3af" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.74 9.74L12.25 12.25" stroke="#9ca3af" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.75 3.5h10.5" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.5 7h7" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5.25 10.5h3.5" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = ({ color = '#115e59' }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.75 8.75v3.5h10.5v-3.5" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.08 5.83L7 8.75" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.92 5.83L7 8.75" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 1.75v7" stroke={color} strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrendUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.5 8.5L4.5 5.5L6.5 7.5L10.5 3.5" stroke="#00a666" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.5 3.5H10.5V6.5" stroke="#00a666" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.67 1.17v2.33" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.33 1.17v2.33" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="1.75" y="2.33" width="10.5" height="10.5" rx="1.17" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.75 5.83h10.5" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#5b5a64" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ---- Status Badge ---- */
function StatusBadge({ status }) {
  const styles = {
    Paid: { bg: '#e8fdf5', color: '#115e59' },
    Pending: { bg: '#fef3c7', color: '#d97706' },
    // Drafts are not owed money - deliberately grey, not the amber of Pending.
    Draft: { bg: '#f1f5f9', color: '#475569' },
  };
  const s = styles[status] || styles.Paid;
  return (
    <span
      className="inline-flex items-center rounded-full font-medium"
      style={{ backgroundColor: s.bg, color: s.color, fontSize: 11, padding: '3px 8px', lineHeight: '13px' }}
    >
      {status}
    </span>
  );
}

/* ---- KPI Card ---- */
function KPICard({ label, value, badge, periodLabel }) {
  return (
    <div className="flex flex-col rounded-[10px] border border-[#e5e5e5] bg-white overflow-hidden" style={{ flex: '1 1 0' }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-[14px] py-[12px] border-b border-[#e5e5e5]">
        <span className="font-medium text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 12, lineHeight: 1.5 }}>
          {label}
        </span>
        {periodLabel ? (
          <span
            className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
            style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
          >
            {periodLabel}
          </span>
        ) : null}
      </div>
      {/* Body */}
      <div className="flex flex-col items-end justify-end flex-1 px-[14px] pt-[20px] pb-[14px] gap-[14px]">
        <span className="text-[#0e1e20] self-start" style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 30, fontWeight: 500, lineHeight: 1 }}>
          {value}
        </span>
        {badge}
      </div>
    </div>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const initials = ((user?.firstName || 'U')[0] + ((user?.lastName || '')[0] || '')).toUpperCase();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [mediatorFilter, setMediatorFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [mediatorsList, setMediatorsList] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingSummary, setBillingSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState(null);
  const [invoiceLoader, setInvoiceLoader] = useState(null);

  const generateInvoiceUrl = async (row) => {
    if (invoiceLoader) return;
    const invoiceId = row.invoiceId;
    if (!invoiceId) {
      const url = row.rawStatus?.startsWith('paid') ? (row.paymentUrl || row.invoiceUrl) : row.invoiceUrl;
      if (url) window.open(url, '_blank');
      return;
    }
    setInvoiceLoader(row.paymentId);
    try {
      const type = row.rawStatus === 'paid' || row.rawStatus === 'paid_manually' ? 'link' : 'pdf';
      const res = await api.post(`/cases/payments/${row.paymentId}/stripe-link`, {
        mediator_user_id: row.mediatorUserId,
        type,
        // GSC/hourly rows carry a bare hourly_invoice_payment id — tell the BE
        // which table to look in (shares numeric id space with payments).
        source: row.source,
      });
      const url = res?.data?.data?.url || res?.data?.data?.invoice_url || res?.data?.data?.payment_url;
      if (url) window.open(url, '_blank');
    } catch (err) {
      console.error('Invoice URL generation failed:', err);
    } finally {
      setInvoiceLoader(null);
    }
  };
  const [pageSize, setPageSize] = useState(10);

  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'billing.invoices',
    { caseName: 200, mediator: 180, invoice: 150, invoiceTo: 220, date: 130, paymentDate: 130, amount: 120 }
  );
  const { sortedRows: sortedInvoices, sort: tableSort, requestSort: requestTableSort } = useSortableData(
    invoices,
    {
      caseName: (r) => r.caseName,
      mediator: (r) => r.mediator,
      invoice: (r) => r.invoice,
      invoiceTo: (r) => r.invoiceTo,
      date: (r) => r.dateRaw,
      // Payment Date = when the invoice was paid. For checks / manual payments
      // this is the moment it was marked Paid Manually (scale-be stamps paid_at
      // / payment_date on the mark). Pending & draft rows have no paid date.
      paymentDate: (r) => r.paymentDateRaw,
      amount: (r) => r.amountRaw,
      status: (r) => r.status,
    }
  );

  // Minimum width the table needs to show every column without the flex-1
  // Status column collapsing into Action. When the viewport is narrower than
  // this the table scrolls horizontally instead of overlapping (the Payment
  // Date column pushed the total past some viewports). 50 = download icon col,
  // 120 = Status min, 60 = Action.
  const tableMinWidth =
    colWidths.caseName + colWidths.mediator + colWidths.invoice + colWidths.invoiceTo +
    colWidths.date + colWidths.paymentDate + colWidths.amount + 50 + 120 + 60;

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    api.get('/mediators?limit=200')
      .then(res => setMediatorsList((res.data?.data || []).filter(m => m.name?.trim())))
      .catch(console.error);
  }, []);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, mediatorFilter, dateFilter, customStart, customEnd]);

  useEffect(() => {
    setLoading(true);
    const handler = setTimeout(() => {
    const params = new URLSearchParams({ page: currentPage, limit: pageSize });
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter) params.set('status', statusFilter);
    if (mediatorFilter) params.set('mediatorId', mediatorFilter);
    const { startDate, endDate } = dateRangeForFilter(dateFilter, customStart, customEnd);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    // Reuse the same filter params for the summary KPIs so the tiles
    // (Admin Fee / Mediator Revenue / Outstanding) narrow with the
    // visible list. Without this the cards stayed at all-time totals
    // while the table below them filtered.
    const summaryParams = new URLSearchParams();
    if (searchQuery) summaryParams.set('search', searchQuery);
    if (statusFilter) summaryParams.set('status', statusFilter);
    if (mediatorFilter) summaryParams.set('mediatorId', mediatorFilter);
    if (startDate) summaryParams.set('startDate', startDate);
    if (endDate) summaryParams.set('endDate', endDate);
    const summaryQs = summaryParams.toString();
    Promise.all([
      api.get(`/billing?${params.toString()}`).catch(() => null),
      api.get(`/billing/summary${summaryQs ? `?${summaryQs}` : ''}`).catch(() => null),
    ]).then(([billingRes, summaryRes]) => {
      if (billingRes?.data?.data) {
        // Med. Date = mediation date from the linked case, falling back to
        // the invoice due date when mediation isn't scheduled yet.
        const fmtDate = (v) => {
          if (!v) return '';
          // A 10-char value is a zone-free business DATE (mediation_date) —
          // render it literally. A longer value is a timestamptz (dueDate) —
          // project to Pacific (matches Scale) so the day doesn't shift for
          // non-Pacific viewers.
          if (String(v).length === 10) {
            const [y, m, day] = v.split('-').map(Number);
            const d = new Date(y, m - 1, day);
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
          }
          const d = new Date(v);
          return isNaN(d.getTime())
            ? ''
            : d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'short', day: 'numeric' });
        };
        const mapped = billingRes.data.data.map(inv => ({
          id: inv.invoiceNumber || `INV-${String(inv.id || 0).padStart(4, '0')}`,
          caseName: inv.caseName || inv.caseNumber || '',
          mediator: inv.mediatorName || inv.invoiceCustomerName || '-',
          invoice: inv.invoiceNumber || '-',
          invoiceTo: inv.invoiceEmails || '-',
          date: fmtDate(inv.mediationDate) || fmtDate(inv.dueDate) || '-',
          dateRaw: inv.mediationDate || inv.dueDate || '',
          // Payment Date = the date the invoice was paid (paid_at for fixed,
          // payment_date for hourly/GSC). For checks / manual payments this is
          // the date it was marked Paid Manually. Blank when not yet paid.
          paymentDate: fmtDate(inv.paidAt) || '',
          paymentDateRaw: inv.paidAt || '',
          amount: `$${Number(inv.grandTotal || inv.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
          amountRaw: Number(inv.grandTotal || inv.amount || 0),
          // BE returns 'Paid', 'Pending', or 'Paid Manually'. Treat the
          // manual case as Paid so paid-manually invoices don't show as
          // Pending in the table (mismatch with the Outstanding KPI).
          // BE returns 'Draft' for invoice_history rows; keep it, or the
          // catch-all below would relabel every draft as Pending.
          status:
            inv.status === 'Draft'
              ? 'Draft'
              : (inv.status || '').toLowerCase().startsWith('paid')
                ? 'Paid'
                : 'Pending',
          invoiceUrl: inv.invoiceUrl || null,
          // Drafts open in the existing editor rather than offering payment
          // actions: /hourly-billing/:caseId?draft_invoice=:uuid
          draftUuid: inv.draftUuid || null,
          caseId: inv.caseId || null,
          // Fields needed by PaymentActionMenu — keep them on the row so
          // the menu can call /cases/payments/:id/{status,stripe-link}.
          paymentId: inv.id,
          // invoiceId is the gate that lets "Paid Manually" enable for a
          // pending row — without it the menu falls into the disabled
          // branch (matches scale-portal's hasInvoice = invoice_id != null
          // check).  Previously this field never reached the row, so every
          // pending invoice rendered the menu item disabled with the
          // "Invoice must be created" tooltip.
          invoiceId: inv.invoiceId ?? null,
          paymentUrl: inv.paymentUrl || null,
          rawStatus: inv.rawStatus || (inv.status || '').toLowerCase(),
          mediatorUserId: inv.mediatorId || null,
          chequeImageUrl: inv.chequeImageUrl || null,
          // GSC (hourly_invoice_payment) rows: carry source so the action menu
          // can target the right table, plus the admin fee (kept separate from
          // the invoice amount).
          source: inv.source || 'payment',
          isGsc: !!inv.isGsc,
          adminFee: inv.adminFee ?? null,
        }));
        setInvoices(mapped);
        setTotalEntries(billingRes.data.total || 0);
        setTotalPages(billingRes.data.pages || 1);
      }
      if (summaryRes?.data?.data) {
        setBillingSummary(summaryRes.data.data);
      } else if (summaryRes?.data) {
        setBillingSummary(summaryRes.data);
      }
    }).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handler);
  }, [currentPage, pageSize, searchQuery, statusFilter, mediatorFilter, dateFilter, customStart, customEnd, refreshKey]);

  return (
    <div className="flex flex-col w-full min-h-screen bg-white relative">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[10000] px-4 py-2 rounded-md shadow-lg text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-[#115e59] text-white'
          }`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {toast.message}
        </div>
      )}
      {/* Header Bar - 60px */}
      <div
        className="flex items-center justify-between border-b border-[#e5e5e5] shrink-0"
        style={{ height: 60, padding: '14px 24px' }}
      >
        <span className="font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 14, lineHeight: 1.5 }}>
          Billings
        </span>
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div
            className="flex items-center justify-center rounded-full bg-[#115e59] shrink-0"
            style={{ width: 32, height: 32 }}
          >
            <span className="font-semibold text-white" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>{initials}</span>
          </div>
        </div>
      </div>

      {/* Filters (above tiles, drives both /billing/summary KPIs and /billing list) */}
      <div style={{ padding: '20px 24px 0 24px' }}>
        <FilterBar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by case ID, name, or mediator..."
          dateValue={dateFilter}
          onDateChange={setDateFilter}
          dateOptions={DEFAULT_DATE_PRESETS}
          dateDefaultValue="all"
          customStart={customStart}
          customEnd={customEnd}
          onCustomDateChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }}
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={[{ label: 'Paid', value: 'Paid' }, { label: 'Pending', value: 'Pending' }, { label: 'Draft', value: 'Draft' }]}
          mediatorValue={mediatorFilter}
          onMediatorChange={setMediatorFilter}
          mediators={mediatorsList}
        />
      </div>

      {/* Metrics Section */}
      <div className="flex gap-3 border-b border-[#e5e5e5]" style={{ padding: '20px 24px 26px 24px' }}>
        {(() => {
          // Period chip text \u2014 reflects the active filter range so the
          // tiles show what slice they're describing.
          const presetLabel = (DEFAULT_DATE_PRESETS.find((p) => p.value === dateFilter) || {}).label;
          const periodLabel =
            dateFilter === 'custom' && (customStart || customEnd)
              ? `${customStart || '\u2026'} \u2013 ${customEnd || '\u2026'}`
              : (presetLabel || 'All time');
          const renderBadge = (change) => {
            const dir = change?.direction || 'flat';
            const pct = change?.percent;
            const isNew = change?.isNew;
            const bg = dir === 'down' ? '#fde8e9' : '#e8fdf5';
            const color = dir === 'down' ? '#dc2626' : '#00a666';
            let label;
            if (isNew) {
              label = `+${change.value} new`;
            } else if (pct == null) {
              label = '\u2014';
            } else {
              const sign = pct > 0 ? '+' : '';
              label = `${sign}${pct}%`;
            }
            const compareLabel = change?.compareLabel || 'last month';
            return (
              <div className="flex items-center gap-[5px] rounded-full" style={{ backgroundColor: bg, padding: '4px 8px 5px 8px' }}>
                {dir === 'down' ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1.5 3.5L4.5 6.5L6.5 4.5L10.5 8.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.5 8.5H10.5V5.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <TrendUpIcon />
                )}
                <div className="flex items-center gap-[3px]">
                  <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 10, fontWeight: 500, color }}>{label}</span>
                  <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 10, fontWeight: 500, color: '#0e1e20' }}>vs</span>
                  <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 10, fontWeight: 500, color: '#0e1e20' }}>{compareLabel}</span>
                </div>
              </div>
            );
          };
          const fmt = (v) =>
            v != null ? `$${Math.round(Number(v) || 0).toLocaleString('en-US')}` : (loading ? '\u2014' : '$0');
          const adminFeeValue = billingSummary?.cmFee ?? billingSummary?.adminFee;
          // Mediator Revenue = INVOICED (paid + pending + paid_manually, incl.
          // hourly/GSC). Prefer the BE's invoiced field; fall back to summing
          // collected (paid) + outstanding (pending) for older BE responses.
          const mediatorRevenueValue =
            billingSummary?.mediatorRevenueInvoiced ??
            ((Number(billingSummary?.mediatorRevenue ?? billingSummary?.collected) || 0) +
              (Number(billingSummary?.mediatorRevenueOutstanding ?? billingSummary?.outstanding) || 0));
          const mediatorOutstandingValue =
            billingSummary?.mediatorRevenueOutstanding ?? billingSummary?.outstanding;
          const adminFeeChange = billingSummary?.change?.cmFee ?? billingSummary?.change?.adminFee;
          const revenueChange =
            billingSummary?.change?.mediatorRevenueInvoiced ??
            billingSummary?.change?.mediatorRevenue ?? billingSummary?.change?.collected;
          const outstandingChange =
            billingSummary?.change?.mediatorRevenueOutstanding ?? billingSummary?.change?.outstanding;
          return (
            <>
              <KPICard
                label="Mediator Revenue"
                periodLabel={periodLabel}
                value={fmt(mediatorRevenueValue)}
                badge={renderBadge(revenueChange)}
              />
              <KPICard
                label="Mediator Outstanding Revenue"
                periodLabel={periodLabel}
                value={fmt(mediatorOutstandingValue)}
                badge={renderBadge(outstandingChange)}
              />
              <KPICard
                label="Case Manager Fee"
                periodLabel={periodLabel}
                value={fmt(adminFeeValue)}
                badge={renderBadge(adminFeeChange)}
              />
            </>
          );
        })()}
      </div>

      {/* Table Section */}
      <div className="flex flex-col gap-6 relative" style={{ padding: '24px 24px 32px 24px' }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#5b5a64] font-['Inter']">Loading...</span>
            </div>
          </div>
        )}
        {/* Table */}
        <div className="flex flex-col rounded-lg border border-[#e5e5e5] overflow-hidden">
          <div className="overflow-x-auto">
          <div style={{ minWidth: tableMinWidth }}>
          {/* Table Header */}
          <div className="flex items-center bg-[#f9fafb] border-b border-[#e5e5e5]" style={{ height: 45 }}>
            <div className="flex items-center h-full px-[14px] relative min-w-0" style={{ width: colWidths.caseName }}>
              <SortableHeader sortKey="caseName" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Case Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('caseName')} getCurrentWidth={() => colWidths.caseName} />
            </div>
            <div className="flex items-center h-full px-[14px] relative min-w-0" style={{ width: colWidths.mediator }}>
              <SortableHeader sortKey="mediator" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Mediator Name</SortableHeader>
              <ResizeHandle onResize={setColWidth('mediator')} getCurrentWidth={() => colWidths.mediator} />
            </div>
            <div className="flex items-center h-full px-[14px] relative min-w-0" style={{ width: colWidths.invoice }}>
              <SortableHeader sortKey="invoice" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Invoice #</SortableHeader>
              <ResizeHandle onResize={setColWidth('invoice')} getCurrentWidth={() => colWidths.invoice} />
            </div>
            <div className="flex items-center h-full px-[14px] relative min-w-0" style={{ width: colWidths.invoiceTo }}>
              <SortableHeader sortKey="invoiceTo" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Invoice To</SortableHeader>
              <ResizeHandle onResize={setColWidth('invoiceTo')} getCurrentWidth={() => colWidths.invoiceTo} />
            </div>
            <div className="flex items-center h-full px-[14px] relative min-w-0" style={{ width: colWidths.date }}>
              <SortableHeader sortKey="date" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Med. Date</SortableHeader>
              <ResizeHandle onResize={setColWidth('date')} getCurrentWidth={() => colWidths.date} />
            </div>
            <div className="flex items-center h-full px-[14px] relative min-w-0" style={{ width: colWidths.paymentDate }}>
              <SortableHeader sortKey="paymentDate" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Payment Date</SortableHeader>
              <ResizeHandle onResize={setColWidth('paymentDate')} getCurrentWidth={() => colWidths.paymentDate} />
            </div>
            <div className="flex items-center h-full px-[14px] relative min-w-0" style={{ width: colWidths.amount }}>
              <SortableHeader sortKey="amount" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Amount</SortableHeader>
              <ResizeHandle onResize={setColWidth('amount')} getCurrentWidth={() => colWidths.amount} />
            </div>
            {/* Cheque column removed — upload + view are now inside the
                three-dot action menu on each row. */}
            <div className="flex items-center justify-center h-full px-[14px]" style={{ width: 50 }}>
              <DownloadIcon color="#5b5a64" />
            </div>
            <div className="flex-1 flex items-center h-full px-[14px]" style={{ minWidth: 120 }}>
              <SortableHeader sortKey="status" sort={tableSort} onSort={requestTableSort} className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Status</SortableHeader>
            </div>
            <div className="flex items-center justify-end h-full px-[14px]" style={{ width: 60 }}>
              <span className="font-medium text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12 }}>Action</span>
            </div>
          </div>

          {/* Table Rows */}
          {!loading && invoices.length === 0 && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64] font-['Inter']">No invoices found</span>
            </div>
          )}
          {sortedInvoices.map((row, idx) => (
            <div
              key={idx}
              className="flex items-center border-b border-[#e5e5e5] last:border-b-0"
              style={{ height: 48 }}
            >
              <div className="flex items-center h-full px-[14px] min-w-0" style={{ width: colWidths.caseName }}>
                {/* Case name opens the case. Requested by Nikki Safavi's office:
                    they find a received check by invoice number here, then had
                    to search for the same case again under Cases. */}
                {row.caseId ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/cases/${row.caseId}`)}
                    className="text-[#0e1e20] truncate text-left cursor-pointer"
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 400, background: 'transparent', border: 'none', padding: 0, maxWidth: '100%' }}
                    title="Open case"
                  >
                    {row.caseName}
                  </button>
                ) : (
                  <span className="text-[#0e1e20] truncate" style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 400 }}>{row.caseName}</span>
                )}
              </div>
              <div className="flex items-center h-full px-[14px] min-w-0" style={{ width: colWidths.mediator }}>
                <span className="text-[#0e1e20] truncate" style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 400 }}>{row.mediator}</span>
              </div>
              <div className="flex items-center h-full px-[14px] min-w-0" style={{ width: colWidths.invoice }}>
                <span className="text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 400 }}>{row.invoice}</span>
              </div>
              <div className="flex items-center h-full px-[14px] min-w-0" style={{ width: colWidths.invoiceTo }}>
                <span className="text-[#0e1e20] truncate" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 400 }}>{row.invoiceTo}</span>
              </div>
              <div className="flex items-center h-full px-[14px] min-w-0" style={{ width: colWidths.date }}>
                <span className="text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 400 }}>{row.date}</span>
              </div>
              <div className="flex items-center h-full px-[14px] min-w-0" style={{ width: colWidths.paymentDate }}>
                <span className="text-[#5b5a64]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 400 }}>{row.paymentDate || '—'}</span>
              </div>
              <div className="flex items-center h-full px-[14px] min-w-0" style={{ width: colWidths.amount }}>
                <span className="font-medium text-[#0e1e20]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 13 }}>{row.amount}</span>
              </div>
              {/* Cheque cell removed — Upload Cheque / View Cheque are now
                  reachable via the row's action menu. */}
              <div className="flex items-center justify-center h-full px-[14px]" style={{ width: 50 }}>
                {invoiceLoader === row.paymentId ? (
                  <div className="w-4 h-4 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <button
                    type="button"
                    onClick={() => generateInvoiceUrl(row)}
                    disabled={!row.invoiceUrl && !row.invoiceId && !row.paymentUrl}
                    className={`flex items-center justify-center rounded-md ${(row.invoiceUrl || row.invoiceId || row.paymentUrl) ? 'cursor-pointer hover:bg-[#f1f5f4]' : 'cursor-not-allowed'}`}
                    style={{ width: 28, height: 28, background: 'transparent', border: 'none', padding: 0 }}
                    aria-label="Download invoice"
                  >
                    <DownloadIcon color={(row.invoiceUrl || row.invoiceId || row.paymentUrl) ? '#115e59' : '#9ca3af'} />
                  </button>
                )}
              </div>
              <div className="flex-1 flex items-center h-full px-[14px]" style={{ minWidth: 120 }}>
                <StatusBadge status={row.status} />
              </div>
              <div className="flex items-center justify-end h-full px-[14px]" style={{ width: 60 }}>
                {row.status === 'Draft' ? (
                  <button
                    type="button"
                    onClick={() =>
                      row.caseId &&
                      navigate(
                        `/hourly-billing/${row.caseId}` +
                          (row.draftUuid ? `?draft_invoice=${row.draftUuid}` : '')
                      )
                    }
                    disabled={!row.caseId}
                    className={`rounded-md px-2 py-1 ${row.caseId ? 'cursor-pointer hover:bg-[#f1f5f4] text-[#115e59]' : 'cursor-not-allowed text-[#9ca3af]'}`}
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500, background: 'transparent', border: 'none' }}
                    aria-label="Open draft invoice"
                  >
                    Open
                  </button>
                ) : row.paymentId ? (
                  <PaymentActionMenu
                    payment={{
                      id: row.paymentId,
                      rawStatus: row.rawStatus,
                      paymentUrl: row.paymentUrl,
                      // Threading invoiceId through so the menu can match
                      // scale-portal's "Paid Manually requires an invoice"
                      // gate.  Without this the menu always rendered the
                      // disabled branch on pending rows.
                      invoiceId: row.invoiceId,
                      // GSC rows live in hourly_invoice_payment — the menu must
                      // tell the BE which table to target.
                      source: row.source,
                      isGsc: row.isGsc,
                    }}
                    mediatorUserId={row.mediatorUserId}
                    chequeImageUrl={row.chequeImageUrl}
                    onToast={setToast}
                    onChanged={() => setRefreshKey((k) => k + 1)}
                  />
                ) : null}
              </div>
            </div>
          ))}
          </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalEntries={totalEntries}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>
    </div>
  );
}
